import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmptyState = ({ 
  type = 'all', // 'all', 'folder', 'search'
  folderName = '',
  onCreateFolder = null 
}) => {
  const getEmptyStateContent = () => {
    switch (type) {
      case 'folder':
        return {
          icon: 'FolderOpen',
          title: `No properties in "${folderName}"`,
          description: 'This folder is empty. Move some properties here or browse for new ones to save.',
          primaryAction: {
            text: 'Browse Properties',
            to: '/property-search-browse',
            variant: 'default'
          },
          secondaryAction: null
        };
      
      case 'search':
        return {
          icon: 'Search',
          title: 'No properties found',
          description: 'Try adjusting your search criteria or browse all saved properties.',
          primaryAction: {
            text: 'Clear Filters',
            onClick: () => window.location?.reload(),
            variant: 'outline'
          },
          secondaryAction: {
            text: 'Browse Properties',
            to: '/property-search-browse',
            variant: 'default'
          }
        };
      
      default:
        return {
          icon: 'Heart',
          title: 'No saved properties yet',
          description: `Start building your collection by saving properties you're interested in. You can organize them into folders and compare your favorites.`,
          primaryAction: {
            text: 'Find Properties',
            to: '/property-search-browse',
            variant: 'default'
          },
          secondaryAction: onCreateFolder ? {
            text: 'Create Folder',
            onClick: onCreateFolder,
            variant: 'outline'
          } : null
        };
    }
  };

  const content = getEmptyStateContent();

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      {/* Icon */}
      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-6">
        <Icon name={content?.icon} size={32} className="text-muted-foreground" />
      </div>
      {/* Title */}
      <h3 className="text-xl font-semibold text-foreground mb-3">
        {content?.title}
      </h3>
      {/* Description */}
      <p className="text-muted-foreground mb-8 max-w-md leading-relaxed">
        {content?.description}
      </p>
      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        {content?.primaryAction?.to ? (
          <Link to={content?.primaryAction?.to}>
            <Button
              variant={content?.primaryAction?.variant}
              iconName="Plus"
              iconPosition="left"
              iconSize={16}
            >
              {content?.primaryAction?.text}
            </Button>
          </Link>
        ) : (
          <Button
            variant={content?.primaryAction?.variant}
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
            onClick={content?.primaryAction?.onClick}
          >
            {content?.primaryAction?.text}
          </Button>
        )}

        {content?.secondaryAction && (
          content?.secondaryAction?.to ? (
            <Link to={content?.secondaryAction?.to}>
              <Button variant={content?.secondaryAction?.variant}>
                {content?.secondaryAction?.text}
              </Button>
            </Link>
          ) : (
            <Button
              variant={content?.secondaryAction?.variant}
              onClick={content?.secondaryAction?.onClick}
            >
              {content?.secondaryAction?.text}
            </Button>
          )
        )}
      </div>
      {/* Tips */}
      {type === 'all' && (
        <div className="mt-12 p-4 bg-muted rounded-lg max-w-md">
          <h4 className="font-medium text-foreground mb-2 flex items-center space-x-2">
            <Icon name="Lightbulb" size={16} />
            <span>Pro Tips</span>
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1 text-left">
            <li>• Click the heart icon on any property to save it</li>
            <li>• Create folders to organize different property types</li>
            <li>• Use the compare feature to analyze up to 4 properties</li>
            <li>• Set up alerts for new properties matching your criteria</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default EmptyState;